import React, { createRef, useCallback, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { IS_APP_BUILD } from '../lib/buildTarget';
import { CONTENT_CLEARANCE_CLASS, PANE_FULL_BLEED_HEIGHT_CLASS } from '../lib/bottomNavMetrics';
import { TAB_ROUTES, useTabSwipe } from '../lib/useTabSwipe';
import { restaurarReposoHorizontal } from '../lib/diag/carrilTripwire';
import { RaizActivaProvider } from '../lib/pager/activacion';
import { PanePagerProvider, crearCanalRutaVista } from '../lib/pager/pane';
import Dashboard from '../pages/Dashboard';
import StudyCenter from '../pages/StudyCenter';
import Analytics from '../pages/Analytics';
// tutor-1 (D): la cuarta raiz es el Tutor (PricingPage deja de ser raiz: /upgrade la pinta suelta en App.js).
import TutorPage from '../pages/TutorPage';

/* ── PAGER-2 — EL CARRIL ──────────────────────────────────────────────────────
 *
 * LO QUE ESTA PIEZA MATA. Hasta aquí, cambiar de pestaña en la app significaba
 * DESMONTAR una raíz y MONTAR la otra: la pantalla destino se construía al
 * llegar, con sus fetches, su silueta y su `animate-fade-in-up` — el "madrazo".
 * El swipe sólo podía animar la SALIDA de la que se iba, porque la vecina no
 * existía todavía (está escrito en `lib/useTabSwipe.js`, donde se descartó el
 * paginador por "doble montaje = dobles efectos de arranque").
 *
 * Aquí las cuatro raíces viven PRE-MONTADAS lado a lado. La de enfrente corre
 * TODO lo de hoy; las otras tres quedan DORMIDAS —pintan de semilla y de bodega,
 * CERO red— gracias al contrato de PAGER-1 (`lib/pager/activacion.js`), que cada
 * pantalla ya respeta efecto por efecto. Sin ese contrato esto sería justo el
 * prefetch especulativo que `screenDataCache` prohíbe; con él, una pane dormida
 * que genere UNA petición es un bug.
 *
 * ── EL ROUTER SIGUE MANDANDO ────────────────────────────────────────────────
 * Este componente NO navega. Las cuatro rutas raíz del build de app renderizan
 * este MISMO elemento, así que el router sigue haciendo match y sigue siendo
 * quien dice cuál está activa (por `pathname`); y como el tipo de elemento en
 * esa posición del árbol no cambia entre las cuatro, React actualiza en vez de
 * remontar — exactamente el mecanismo por el que el chasis (header, Sidebar,
 * `<main>`, píldora) ya sobrevivía a un cambio de pestaña.
 *
 * El embudo de navegación tampoco cambia: `nodo.click()` → `<Link>` →
 * react-router. El carril ANIMA, jamás navega.
 *
 * ── LA GEOMETRÍA, Y POR QUÉ NO HAY UN SOLO NÚMERO NUEVO ─────────────────────
 *
 *   viewport   overflow-hidden. Mide lo que medía el contenido del chasis MÁS el
 *              colchón que el chasis se reserva para la píldora: es literalmente
 *              `PANE_FULL_BLEED_HEIGHT_CLASS`, la constante que el Panel ya
 *              usaba para llegar al fondo de la pantalla, con su margen negativo
 *              incluido (por eso el `<main>` no gana scroll: la caja de margen
 *              sigue midiendo lo de siempre).
 *   capa del   el nodo del gesto. Es lo que `useTabSwipe` pinta y mide, así que
 *   gesto      el umbral del 25% se sigue midiendo contra UN viewport, no contra
 *              los cuatro. Los listeners cuelgan de aquí — de su propio
 *              contenedor, nunca de `document` (doctrina S-7).
 *   riel       `flex`, 400% de ancho, y su posición de reposo la escribe REACT:
 *              `left: -100%` por pestaña, o sea un viewport exacto. Sin
 *              transición: un tap en la píldora cambia de pestaña AL INSTANTE,
 *              como hoy. POR QUÉ `left` Y NO UN `transform` — ver abajo, es la
 *              decisión que salva todos los modales de las cuatro raíces.
 *   pane       25% del riel = un viewport, y es SU PROPIO SCROLLER. De ahí salen
 *              dos cosas gratis: el scroll deja de heredarse entre pantallas
 *              distintas (el defecto que `SettingsPage` tuvo que parchear a mano)
 *              y la posición de cada pestaña se conserva al cambiar, porque la
 *              pane no se desmonta. Su colchón inferior es `CONTENT_CLEARANCE_CLASS`,
 *              el mismo del `<main>`: la caja de contenido que ve cada pantalla
 *              mide EXACTAMENTE lo que medía antes, así que el `h-full` de
 *              StudyCenter y el full-bleed del Panel siguen resolviendo igual.
 *
 * Cero safe areas nuevas: las dos constantes de arriba ya las traen dentro
 * (`lib/bottomNavMetrics.js` es la fuente única), y la de arriba la pone el
 * `<main>` del chasis. Este archivo no escribe ni un píxel a mano.
 *
 * ── EN REPOSO NO HAY UN SOLO `transform`, Y ESO NO ES COSMÉTICO ─────────────
 * La posición de reposo del riel es `left`, no `translateX`, y la razón es una
 * regla de CSS que se lleva por delante media app si se ignora: UN ANCESTRO CON
 * `transform` SE VUELVE EL BLOQUE CONTENEDOR DE SUS DESCENDIENTES `position:
 * fixed`. Las cuatro raíces tienen entre todas trece overlays a pantalla
 * completa —la cortina del arrastre de archivos, el visor de imagen, el selector
 * de paquetes, créditos insuficientes, duplicado archivado, el modal de idioma,
 * la conversión de invitado, la cortina de restauración, el documento de
 * invitado vencido, el borrado de datos, la cortina de pago del auto-split y el
 * modal de upgrade— y TODOS son `fixed inset-0` con su `z-index` por encima del
 * header. Con un riel transformado en reposo, `inset-0` dejaría de significar
 * "la pantalla" y pasaría a significar "los cuatro viewports del riel": modales
 * centrados entre la pane 2 y la 3, y encerrados bajo el `z-index` del chasis
 * por el contexto de apilamiento que el transform crea. `left` sobre un elemento
 * `relative` NO crea bloque contenedor ni contexto de apilamiento: en reposo la
 * geometría de un `fixed` es carácter por carácter la de hoy.
 *
 * Y el gesto sí transforma —es lo único que se anima, la doctrina de siempre—,
 * pero sólo mientras el dedo está abajo y durante el asentamiento; y ahí no
 * puede haber un overlay abierto, porque cualquiera de los trece levanta la CAPA
 * de su pantalla y con una capa encima el gesto NO NACE (se pregunta en el
 * `touchstart`). PAGER-3 alargó esa ventana —del `TAB_SLIDE_MS` del commit al
 * arrastre entero— pero no la abrió: sigue vacía por construcción, porque lo que
 * la cierra es el nacimiento del gesto, no su duración.
 *
 * ── LA ACTIVACIÓN ES LA LLEGADA ─────────────────────────────────────────────
 * Durante el recorrido la vecina sigue DORMIDA: se ve su contenido de semilla
 * deslizarse pegado al dedo, que es contenido VIVO y no una silueta. Se activa
 * cuando el router aterriza (el `click()` del final del gesto, o el tap directo),
 * ni un milisegundo antes. Activar por adelantado sería duplicar peticiones a
 * ciegas — y con PAGER-3 sería además pagarlas por cada arrastre del que el
 * usuario se arrepiente.
 *
 * ── EL DEDO PEGADO YA LLEGÓ (PAGER-3) ───────────────────────────────────────
 * Aquí decía que el gesto seguía siendo DISCRETO y que el dedo pegado era otra
 * tanda. Esa tanda ya corrió: el carril sigue al dedo 1:1 desde que el gesto se
 * declara dueño, y al soltar ASIENTA —hacia la vecina o de vuelta al reposo, con
 * resorte visible—. Este archivo no cambió por eso ni una línea de código: la
 * mecánica entera vive en `lib/useTabSwipe.js` y lo único que necesitaba era el
 * nodo que ya le da PAGER-2.
 *
 * ── WEB ─────────────────────────────────────────────────────────────────────
 * No existe. En `!IS_APP_BUILD` las cuatro rutas siguen renderizando su página
 * directo, con sus montajes de siempre y sus seis tabs; este componente devuelve
 * la raíz del `pathname` suelta si alguien lo montara por error, sin carril, sin
 * proveedores y sin gesto.
 */

/** Las cuatro raíces, indexadas por su ruta. El ORDEN y la LISTA no viven aquí:
 *  salen de `TAB_ROUTES` (`lib/useTabSwipe.js`), que es la única fuente del mapa
 *  de pestañas y la misma que usa la píldora. Este objeto sólo dice qué componente
 *  le toca a cada ruta; si mañana entrara una quinta pestaña, este archivo se
 *  pondría rojo por el `RAICES_FALTANTES` de abajo en vez de renderizar un hueco. */
const RAIZ_DE = {
  '/dashboard': Dashboard,
  '/study': StudyCenter,
  '/analytics': Analytics,
  '/tutor': TutorPage,
};

/** Rutas de la píldora sin componente declarado. Vacío = el mapa está completo. */
export const RAICES_FALTANTES = TAB_ROUTES.filter((r) => !RAIZ_DE[r]);

export default function PagerRaices() {
  const { pathname } = useLocation();
  const indice = TAB_ROUTES.indexOf(pathname);
  const idx = indice < 0 ? 0 : indice;

  /* EL CANAL DE CADA PANE, construido UNA vez y estable para siempre: el
     scrollport (para que el aterrizaje de Analíticas escriba en SU scroller y no
     en el del chasis, que ya no scrollea para las raíces) y el reporte de capa
     encima. Va en refs y no en estado a propósito — abrir un modal en el Panel
     no puede re-rendear las cuatro raíces vivas por un booleano que sólo se
     consulta cuando un dedo toca la pantalla. */
  /* ── TANDA 19 (#210) — LA RUTA VISIBLE, PUBLICADA UNA VEZ PARA LAS CUATRO ───
     Uno solo para el carril entero, y por eso "dos barras" no es un estado
     alcanzable: lo que se publica es UNA ruta, no un booleano por pane. Va en el
     canal de cada pane —compartido por referencia— para que la raíz que
     pregunta no necesite un proveedor nuevo ni saber que existe un carril.
     Quién escribe: el gesto, por `publicarRutaVista` (abajo). Quién lee: la raíz
     que pinta algo anclado al viewport, con `useEsRutaVista()`. Y en reposo el
     canal calla, así que sin gesto de por medio manda el router, igual que hoy. */
  /* ── TANDA 21 (#212) — EL MARCO DE CADA PANE, Y POR QUÉ EXISTE ───────────
     ESTA ES LA TERCERA LECTURA DEL MISMO SÍNTOMA, y la que cerró el caso. La
     TANDA 19 arregló el INSTANTE en que el recuadro de estudiar aparece; la 20
     lo sacó del vagón para que dejara de viajar montado en el transform del
     gesto. Con las dos puestas seguía leyendo como "algo que aparece", y el
     motivo es que seguía clavado al VIEWPORT: la hoja de Estudiar entraba
     deslizándose con su título, sus tarjetas y su lista, y el recuadro no venía
     en la hoja. La firma #212 lo redefine: el recuadro es MUEBLE de la página.

     LA MEDICIÓN QUE ELIGIÓ ESTO (Chrome real, 390×844). Con el riel en
     `/dashboard` y el recuadro siendo de `/study`, la pane de estudio está en
     `left: 378` y el recuadro anclado al viewport estaba en `left: 0..390`:
     366 píxeles de desfase. El recuadro no estaba sobre su página —estaba sobre
     el Panel— y por eso hacía falta un gate que lo escondiera. Con el marco,
     pane y recuadro caen en 378 los dos, y en pleno arrastre en 258 los dos.

     CÓMO SE PARTE LA PANE, Y QUÉ SE QUEDA DÓNDE. El MARCO toma la geometría que
     la pane tenía en el riel (`w-1/4 h-full shrink-0`) y suma un `relative`
     para ser la caja a la que el mueble se ancla. El SCROLLER es el de siempre
     y no cede nada: conserva su `data-pager-pane` —que es lo que barre el
     tripwire para curar el `scrollLeft`, y tiene que seguir en el nodo que de
     verdad scrollea—, su `data-testid`, su `scrollportRef` (el que Analíticas
     usa para aterrizar al tope) y su colchón. Medido: la caja del scroller es
     la misma que la de la pane de antes, píxel por píxel.

     Y `relative` NO CAMBIA NADA PARA LOS TRECE OVERLAYS: sólo `transform` y
     compañía crean bloque contenedor para lo anclado al viewport. Medido
     también: un `fixed inset-0` dentro de la pane da 0..844 × 0..390 con marco
     y sin él, y el mismo recorte durante el arrastre.

     LO QUE ESTO RETIRA. La capa fija que la TANDA 20 colgó del carril se va
     entera: era el destino de un solo cliente, y ese cliente ahora se ancla a
     su propia hoja. */
  const marcosRef = useRef(null);
  if (IS_APP_BUILD && !marcosRef.current) {
    marcosRef.current = {};
    for (const ruta of TAB_ROUTES) marcosRef.current[ruta] = createRef();
  }

  const vistaRef = useRef(null);
  const capasRef = useRef(null);
  const canalesRef = useRef(null);
  if (!canalesRef.current) {
    vistaRef.current = crearCanalRutaVista();
    capasRef.current = {};
    canalesRef.current = {};
    for (const ruta of TAB_ROUTES) {
      capasRef.current[ruta] = false;
      canalesRef.current[ruta] = {
        scrollportRef: createRef(),
        reportarCapa: (valor) => { capasRef.current[ruta] = !!valor; },
        ruta,
        rutaVista: vistaRef.current,
        marco: marcosRef.current ? marcosRef.current[ruta] : null,
      };
    }
  }
  const publicarRutaVista = useCallback((ruta) => {
    if (vistaRef.current) vistaRef.current.publicar(ruta);
  }, []);

  /* ── TANDA 12 (#183) — EL RIEL SE POSA SOLO, EN EL INSTANTE DEL COMMIT ──────
     El gesto ya no espera a que React commitee la navegación para dejar de
     transformar: en el mismo frame del `click()` coloca el riel en la posición
     de reposo del destino y suelta su transform. La geometría es de este archivo
     —el mapa de rutas a índices y la unidad de un viewport son suyos—, así que
     lo que el hook recibe es esta función y nada más; él no sabe qué hay dentro
     de su nodo, que es su contrato de siempre.

     Escribe EXACTAMENTE el mismo valor que React escribirá al confirmar (el
     `style` de abajo sale de la misma cuenta), así que cuando la transición
     aterrice no moverá un píxel. Y sigue siendo la propiedad de reposo de
     siempre: aquí nunca se deja un transform puesto, por la razón que explica el
     letrero de la geometría más arriba. Contesta si pudo, para que el hook no
     suelte su transform cuando no hay riel al que posar. */
  const rielRef = useRef(null);
  const aterrizarRiel = useCallback((ruta) => {
    const nodo = rielRef.current;
    if (!nodo) return false;
    const i = TAB_ROUTES.indexOf(ruta);
    if (i < 0) return false;
    try { nodo.style.left = `${i * -100}%`; } catch (e) { return false; }
    return true;
  }, []);


  /* ¿EL ROUTER YA SE MOVIÓ? La otra mitad del aterrizaje atómico, y vive aquí
     por la misma razón: la ruta es asunto del carril, no del gesto. `pushState`
     corre DENTRO del click del `<Link>`, así que en la línea siguiente al
     `click()` esto ya dice el destino si la navegación ocurrió, y sigue diciendo
     la vieja si una compuerta se la tragó (el candado de sesión). Es EXACTAMENTE
     el discriminador que `lib/diag/navLoc.js` documenta — allá para registrar,
     aquí para decidir si el carril puede posarse. Y no es `pathname` de arriba:
     ése es el de React, que en esta ventana todavía va atrás. */
  const yaNavegoA = useCallback((ruta) => {
    try { return !!ruta && window.location.pathname === ruta; } catch (e) { return false; }
  }, []);

  /* EL GESTO, UNA SOLA VEZ PARA LAS CUATRO. Antes lo llamaba cada raíz — cuatro
     juegos de listeners táctiles vivos, uno por pantalla montada. Los umbrales,
     el candado de dirección, las fuentes excluidas y el `click()` de la píldora
     no cambiaron ni un carácter: lo único distinto es QUÉ nodo se pinta. */
  const swipe = useTabSwipe({
    tab: pathname,
    /* La ruta que el gesto le pasa es la que el carril MUESTRA (TANDA 12): con
       un aterrizaje en vuelo, la capa que puede haber encima es la de la
       pantalla que el usuario tiene delante, no la que React todavía pinta.
       Sin ella —el hook usado suelto— se cae al `pathname` de siempre. */
    enabled: (ruta) => !capasRef.current[ruta || rutaRef.current],
    aterrizarRiel,
    yaNavegoA,
    /* TANDA 19: el gesto avisa cuál es la pantalla que se está viendo. Es un
       AVISO y no una orden — el carril no navega por esto, ni cambia una capa,
       ni mueve un píxel: sólo lo repite por el canal de las panes. */
    publicarRutaVista,
  });

  /* ── REMATE-266 / P7 — LA LEY DE REPOSO SE RESTAURA AL ATERRIZAR ────────────
     El síntoma que el tripwire caza (`lib/diag/carrilTripwire.js`) es una
     pantalla CORRIDA que dura minutos y que sólo se quita matando la app, porque
     nada en el producto vuelve a poner en cero un desplazamiento HORIZONTAL:
     cambiar de pestaña no lo toca, ya que la posición de scroll de cada pane se
     conserva a propósito (ver la nota de la geometría, arriba).

     Aquí se cierra por el lado barato: cada vez que el router aterriza en otra
     pestaña, las capas del carril vuelven a `scrollLeft: 0`. Efecto DE LAYOUT
     —igual que el aterrizaje del gesto— para que la corrección caiga en el mismo
     frame en que React coloca el riel y el usuario no vea un paso intermedio.

     LO QUE NO TOCA, y es la mitad importante: `scrollTop`. Que cada pane
     conserve su posición VERTICAL es una propiedad del carril, no un accidente.
     Tampoco toca clases, reglas de CSS ni contenedores de scroll (COS-33). */
  useLayoutEffect(() => {
    if (!IS_APP_BUILD) return;
    restaurarReposoHorizontal();
  }, [idx]);

  if (!IS_APP_BUILD) {
    const Suelta = RAIZ_DE[pathname];
    return Suelta ? <Suelta /> : null;
  }

  return (
    <div
      className={`relative w-full overflow-hidden ${PANE_FULL_BLEED_HEIGHT_CLASS}`}
      data-testid="pager-carril"
    >
      <div {...swipe} className="h-full w-full" data-testid="pager-gesto">
        <div
          ref={rielRef}
          className="relative flex h-full w-[400%]"
          style={{ left: `${idx * -100}%` }}
          data-testid="pager-riel"
        >
          {TAB_ROUTES.map((ruta) => {
            const Raiz = RAIZ_DE[ruta];
            const canal = canalesRef.current[ruta];
            return (
              /* EL MARCO: la caja de la hoja en el riel, y el ancla de sus
                 muebles. No scrollea y no pinta nada por su cuenta. */
              <div
                key={ruta}
                ref={canal.marco}
                className="relative w-1/4 h-full shrink-0"
                data-testid={`pager-marco-${ruta.slice(1)}`}
              >
                {/* EL SCROLLER, el de siempre: mismas clases, mismo colchón,
                    mismo `data-pager-pane` (el tripwire barre por ahí) y mismo
                    ref. Lo único que cambia es su ancho declarado, que ahora lo
                    da el marco: `w-1/4` del riel → `w-full` del marco. */}
                <div
                  ref={canal.scrollportRef}
                  className={`w-full h-full overflow-y-auto overflow-x-hidden overscroll-contain ${CONTENT_CLEARANCE_CLASS} lg:pb-8`}
                  data-pager-pane={ruta}
                  data-testid={`pager-pane-${ruta.slice(1)}`}
                >
                  <PanePagerProvider valor={canal}>
                    <RaizActivaProvider activa={pathname === ruta}>
                      <Raiz />
                    </RaizActivaProvider>
                  </PanePagerProvider>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
